import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, IconButton, Tooltip, CircularProgress, Stack, Alert
} from '@mui/material';
import { Edit, Refresh } from '@mui/icons-material';
import { getEmployeeFormAssignments } from '../FormManagement/FormManagementQueries';
import { useNavigate } from 'react-router-dom';

const EmployeeReviewsIndex = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  const loadAssignedForms = async () => {
    setLoading(true);
    try {
      const result = await getEmployeeFormAssignments();
      const formsList = result?.forms || result?.Items || [];
      setForms(formsList);
    } catch (error) {
      console.error('Error loading assigned forms:', error);
      setAlert({ open: true, message: 'Failed to load assigned forms', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignedForms();
  }, []);

  const getFormTypeChip = (type) => {
    const colors = {
      'annual': 'primary',
      'probation': 'secondary',
    };
    const labels = {
      'annual': 'Annual Review',
      'probation': 'Probation Review',
    };
    return <Chip label={labels[type] || type} color={colors[type] || 'default'} size="small" />;
  };

  const getStatusChip = (status) => {
    const colors = {
      'draft': 'default',
      'active': 'success',
      'closed': 'error',
      'archived': 'warning',
    };
    return <Chip label={status || 'draft'} color={colors[status] || 'default'} size="small" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    const getOrdinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    
    return `${month} ${day}${getOrdinal(day)} ${year}`;
  };

  const handleOpenForm = (form) => {
    // Parse form schema
    let formSchema = {};
    try {
      formSchema = typeof form.form_schema === 'string' ? JSON.parse(form.form_schema) : form.form_schema;
    } catch (error) {
      console.error('Error parsing form schema:', error);
    }

    // Navigate to appropriate review page based on form type
    if (form.form_type === 'annual') {
      navigate('/AnnualReview', { 
        state: { 
          formId: form.id, 
          formData: form,
          formSchema: formSchema
        } 
      });
    } else if (form.form_type === 'probation') {
      navigate('/ProbationReview', { 
        state: { 
          formId: form.id, 
          formData: form,
          formSchema: formSchema
        } 
      });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={2} sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>My Performance Reviews</Typography>
          <Tooltip title="Refresh">
            <IconButton onClick={loadAssignedForms} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>

        {alert.open && (
          <Alert severity={alert.severity} onClose={() => setAlert({ ...alert, open: false })} sx={{ mb: 2 }}>
            {alert.message}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : forms.length === 0 ? (
          <Box py={4} display="flex" justifyContent="center">
            <Typography variant="body2" color="text.secondary">
              No reviews assigned to you yet
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Form Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {forms.map((form) => (
                <TableRow key={form.id} hover>
                  <TableCell>{form.form_name || 'Untitled Form'}</TableCell>
                  <TableCell>{getFormTypeChip(form.form_type)}</TableCell>
                  <TableCell>{formatDate(form.start_date)}</TableCell>
                  <TableCell>{formatDate(form.end_date)}</TableCell>
                  <TableCell>{getStatusChip(form.status)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Open Review">
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleOpenForm(form)}
                        disabled={form.status === 'closed' || form.status === 'archived'}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default EmployeeReviewsIndex;
